import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Period } from '@/shared/types/dashboard'
import { useState, useEffect } from 'react'
import { getPeriodLabel } from '@/shared/utils/period'
import { useDashboardStore } from '@/core/stores/dashboardStore'

interface DashboardHeaderProps {
  selectedPeriod: Period
  onPeriodChange: (
    period: Period,
    customDates?: { startDate: string; endDate: string }
  ) => void
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

export default function SelectPeriod({
  selectedPeriod,
  onPeriodChange,
}: DashboardHeaderProps) {
  const { customDateRange, setCustomDateRange } = useDashboardStore()

  const [startDate, setStartDate] = useState(
    customDateRange?.startDate || new Date().toISOString().split('T')[0]
  )
  const [endDate, setEndDate] = useState(customDateRange?.endDate || '')
  const [showModal, setShowModal] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (customDateRange) {
      setStartDate(customDateRange.startDate)
      setEndDate(customDateRange.endDate)
    }
  }, [customDateRange])

  const handlePeriodChange = (period: Period) => {
    if (period !== 'custom') {
      onPeriodChange(period)
      setShowModal(false)
      setError('')
      return
    }

    setShowModal(true)
  }

  const handleCloseModal = (confirm = false) => {
    if (!confirm) {
      setError('')
      setShowModal(false)
      return
    }

    if (!startDate || !endDate) {
      setError('Por favor, selecione as datas inicial e final.')
      return
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('A data inicial não pode ser maior que a final.')
      return
    }

    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays > 7) {
      setError('O período personalizado não pode ser maior que 7 dias.')
      return
    }

    setError('')
    setShowModal(false)
    const customDates = { startDate, endDate }
    setCustomDateRange(customDates)
    onPeriodChange('custom' as Period, customDates)
  }

  return (
    <>
      <div className="flex items-center gap-4 w-full">
        <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-full">
            <SelectValue>
              {selectedPeriod === 'custom' && (startDate || endDate) ? (
                <span>
                  Período: {formatDate(startDate)}
                  {startDate && endDate ? ' - ' : ''}
                  {formatDate(endDate)}
                </span>
              ) : (
                getPeriodLabel(selectedPeriod)
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={Period.Today}>Hoje</SelectItem>
            <SelectItem value={Period.Yesterday}>Ontem</SelectItem>
            <SelectItem value={Period.ThisWeek}>Este Ciclo</SelectItem>
            <SelectItem value={Period.LastWeek}>Ciclo Passado</SelectItem>
            <SelectItem value={Period.ThisMonth}>Este Mês</SelectItem>
            <SelectItem value={Period.LastMonth}>Mês Passado</SelectItem>
            <SelectItem value={Period.Custom}>Personalizado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background p-4 rounded-md shadow-md w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-2 text-text-title">
              Escolha o período
            </h2>
            <div className="flex items-start gap-2 mb-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-text-title block mb-1">
                  Inicial
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border px-2 py-1 rounded-md w-full"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-text-title block mb-1">
                  Final
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border px-2 py-1 rounded-md w-full"
                />
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
            <div className="flex justify-between items-center">
              <button
                onClick={() => {
                  setShowModal(false)
                }}
                className="px-4 py-1 bg-pinkGarupa text-white rounded border"
              >
                Fechar
              </button>

              <button
                onClick={() => {
                  handleCloseModal(true)
                }}
                className="bg-greenGarupa text-white px-4 py-1 rounded"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
